import { useEffect, useRef, useState } from 'react'
import { Mic, MicOff, PhoneCall, Volume2 } from 'lucide-react'
import { useLiveKit } from '@/lib/livekit'
import { useMeetingStore } from '@/hooks'

interface MeetingMiniViewProps {
  currentTab: string
  onReturnToMeeting: () => void
}

// Component to render audio for a single remote participant
function RemoteAudio({ audioTrack, volume = 1 }: { audioTrack: any; volume?: number }) {
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (audioTrack && audioRef.current) {
      audioTrack.attach(audioRef.current)
      return () => {
        audioTrack.detach(audioRef.current!)
      }
    }
  }, [audioTrack])

  // Apply volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.min(Math.max(volume, 0), 2)
    }
  }, [volume])

  return <audio ref={audioRef} autoPlay />
}

// Local storage key for master volume
const MASTER_VOLUME_KEY = 'meeting-master-volume'

// Load saved master volume from localStorage
const loadMasterVolume = (): number => {
  try {
    const saved = localStorage.getItem(MASTER_VOLUME_KEY)
    return saved ? parseFloat(saved) : 1
  } catch {
    return 1
  }
}

export function MeetingMiniView({ currentTab, onReturnToMeeting }: MeetingMiniViewProps) {
  const {
    isConnected,
    participants,
    remoteParticipants,
    localParticipant,
    isMuted,
    toggleMute,
  } = useLiveKit()

  const { roomId } = useMeetingStore()
  const [masterVolume, setMasterVolume] = useState(loadMasterVolume)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Save master volume to localStorage
  const handleMasterVolumeChange = (volume: number) => {
    setMasterVolume(volume)
    localStorage.setItem(MASTER_VOLUME_KEY, volume.toString())
  }

  // Volume slider hover handlers
  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current)
      volumeTimeoutRef.current = null
    }
    setShowVolumeSlider(true)
  }

  const handleVolumeMouseLeave = () => {
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false)
    }, 500)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current)
      }
    }
  }, [])

  // Render audio elements with master volume applied
  const remoteAudioElements = (
    <div className="hidden">
      {remoteParticipants.map((participant) => (
        participant.audioTrack && (
          <RemoteAudio
            key={participant.identity}
            audioTrack={participant.audioTrack}
            volume={masterVolume}
          />
        )
      ))}
      {/* Also render screen share audio */}
      {remoteParticipants.map((participant) => (
        participant.screenAudioTrack && (
          <RemoteAudio
            key={`screen-${participant.identity}`}
            audioTrack={participant.screenAudioTrack}
            volume={masterVolume}
          />
        )
      ))}
    </div>
  )

  // If not connected, nothing to render
  if (!isConnected) {
    return null
  }

  // Always render audio, but only show mini-view when NOT on meetings tab
  if (currentTab === 'meetings') {
    return remoteAudioElements
  }

  return (
    <>
      {/* Hidden audio elements for remote participants */}
      {remoteAudioElements}

      {/* Visible mini-view - Audio Mixer style */}
      <div className="fixed bottom-6 left-[17rem] z-40 w-56 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header with room info and controls */}
        <div className="px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-element)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-[var(--text-main)]">
                {roomId?.slice(0, 8)}...
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Master volume control */}
              <div
                className="relative"
                onMouseEnter={handleVolumeMouseEnter}
                onMouseLeave={handleVolumeMouseLeave}
              >
                <button
                  className="p-1 bg-[var(--bg-card)] hover:bg-[var(--bg-hover)] rounded transition-colors"
                  title="Master volume"
                >
                  <Volume2 size={14} className="text-[var(--text-muted)]" />
                </button>
                {showVolumeSlider && (
                  <div
                    className="absolute bottom-full right-0 mb-2 p-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg min-w-[140px] shadow-xl"
                    onMouseEnter={handleVolumeMouseEnter}
                    onMouseLeave={handleVolumeMouseLeave}
                  >
                    <div className="text-[10px] text-[var(--text-muted)] mb-2 uppercase tracking-wide">Master Volume</div>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.05"
                      value={masterVolume}
                      onChange={(e) => handleMasterVolumeChange(parseFloat(e.target.value))}
                      className="w-full h-2 bg-[var(--bg-element)] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[var(--primary)] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                    />
                    <div className="text-xs text-[var(--text-main)] text-center mt-2 font-medium">
                      {Math.round(masterVolume * 100)}%
                    </div>
                  </div>
                )}
              </div>
              {/* Mic toggle */}
              <button
                onClick={toggleMute}
                className={`p-1 rounded transition-colors ${
                  isMuted
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-[var(--bg-card)] text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff size={14} /> : <Mic size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Audio mixer - participant list with levels */}
        <div className="max-h-48 overflow-y-auto">
          {participants.map((participant) => (
            <div
              key={participant.identity}
              className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)] last:border-b-0"
            >
              {/* Mic indicator */}
              <div className={`flex-shrink-0 ${participant.isMuted ? 'text-red-400' : 'text-green-400'}`}>
                {participant.isMuted ? <MicOff size={12} /> : <Mic size={12} />}
              </div>

              {/* Name */}
              <div className="flex-1 min-w-0">
                <span className="text-xs text-[var(--text-main)] truncate block">
                  {participant.name}
                  {participant.isLocal && ' (You)'}
                </span>
              </div>

              {/* Audio level meter */}
              {!participant.isMuted && (
                <div className="flex items-end gap-0.5 h-3 w-8">
                  {[0.1, 0.25, 0.4, 0.6].map((threshold, i) => (
                    <div
                      key={i}
                      className={`w-1 rounded-full transition-all duration-75 ${
                        participant.audioLevel > threshold ? 'bg-green-500' : 'bg-[var(--bg-element)]'
                      }`}
                      style={{ height: `${(i + 1) * 25}%` }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Return to call button */}
        <div className="p-2 border-t border-[var(--border)]">
          <button
            onClick={onReturnToMeeting}
            className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-[var(--primary)] hover:opacity-90 text-white text-xs font-medium rounded-lg transition-opacity"
          >
            <PhoneCall size={12} />
            Return to Call
          </button>
        </div>
      </div>
    </>
  )
}
